import { call, put } from 'redux-saga/effects';
import {
    fetchActiveRunsSuccess,
    fetchWorkspaces,
    fetchWorkspacesFail,
    fetchWorkspacesSuccess,
    workspaceActionFail,
    workspaceActionSuccess
} from './actions';
import {
    createWorkspace,
    deleteWorkspace,
    getRuns,
    getWorkspaces,
    refreshWorkspace,
    resetWorkspace
} from '../simulation.backend';
import {
    makeErrorPayload,
    makeSuccessPayload
} from '../../../../../shared/helpers/payload.helper';
import {
    setErrorToast,
    setSuccessToast
} from '../../../../../shared/toast/redux/actions';
import { fetchJurisdictions } from '../../../../../jurisdiction/redux/actions';
import { CREATE_KEY_PREFIX } from './reducer';
import { countActiveRunsByWorkspace } from '../../simulation.model';
import { defaultFirstPage } from '../../../../../services/Paging';

/* A sandbox keeps a handful of runs, so this is far more than all the runs there are; the cap is for a backend that has gone wrong. */
export const RUNS_PAGE_SIZE = 100;
export const MAX_RUNS_PAGES = 20;

export function* fetchWorkspacesSaga() {
    try {
        const response = yield call(getWorkspaces);
        yield put(fetchWorkspacesSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchWorkspacesFail(formattedError));
        yield put(setErrorToast(formattedError));
        return;
    }
    yield call(fetchActiveRunsSaga);
}

/*
  Reads the runs, all the pages of them, to know which sandboxes have one that is queued or running: the list of sandboxes does not
  say. This only lets the screen disable what the server would refuse, so a read that fails is not told to the user and leaves nothing
  disabled: the server still refuses, and the screen shows what it says (WORKSPACE_BUSY).
*/
export function* fetchActiveRunsSaga() {
    try {
        const runs = [];

        for (
            let page = defaultFirstPage;
            page < defaultFirstPage + MAX_RUNS_PAGES;
            page++
        ) {
            const response = yield call(getRuns, {
                page,
                pageSize: RUNS_PAGE_SIZE
            });

            runs.push(...(response.data.result || []));
            if (!response.data.hasNext) {
                break;
            }
        }
        yield put(fetchActiveRunsSuccess(countActiveRunsByWorkspace(runs)));
    } catch (error) {
        yield put(fetchActiveRunsSuccess({}));
    }
}

/*
  Does a call on a workspace and, whichever way it ends, says so. When it worked the list of workspaces is read again, and so is
  the list of jurisdictions the selector at the top is made from, as a workspace is a jurisdiction there.
*/
function* doWorkspaceAction({ key, apiCall, successMessage }) {
    try {
        const response = yield call(apiCall);
        yield put(workspaceActionSuccess({ key }));
        yield put(
            setSuccessToast(makeSuccessPayload(successMessage(response)))
        );
        yield put(fetchWorkspaces());
        yield put(fetchJurisdictions({ keepSelection: true }));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(workspaceActionFail({ key, error: formattedError }));
        yield put(setErrorToast(formattedError));
    }
}

export function* createWorkspaceSaga(action) {
    const { sourceCode } = action.payload;

    yield call(doWorkspaceAction, {
        key: CREATE_KEY_PREFIX + sourceCode,
        apiCall: () => createWorkspace(sourceCode),
        successMessage: response =>
            `Sandbox ${response.data.code} created from ${sourceCode}.`
    });
}

export function* refreshWorkspaceSaga(action) {
    const { code } = action.payload;

    yield call(doWorkspaceAction, {
        key: code,
        apiCall: () => refreshWorkspace(code),
        successMessage: () => `The rules of sandbox ${code} were refreshed.`
    });
}

export function* resetWorkspaceSaga(action) {
    const { code } = action.payload;

    yield call(doWorkspaceAction, {
        key: code,
        apiCall: () => resetWorkspace(code),
        successMessage: () => `The data of sandbox ${code} was deleted.`
    });
}

export function* deleteWorkspaceSaga(action) {
    const { code } = action.payload;

    yield call(doWorkspaceAction, {
        key: code,
        apiCall: () => deleteWorkspace(code),
        successMessage: () => `Sandbox ${code} was deleted.`
    });
}
